import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel, useTheme, TextField, Grid, Tabs, Tab, Stack, Card, CardContent, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { apiClient } from '../contexts/AuthContext';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { addMonths } from 'date-fns';

interface MonthlyStats {
  auxiliaire: string;
  category: string;
  totalHours: number;
  nightHours: number;
  sundayHours: number;
  hourlyRate: number;
  nightlyRate: number;
  totalCost: number;
}

interface CategoryStats {
  category: string;
  totalHours: number;
  nightHours: number;
  sundayHours: number;
  totalCost: number;
}

interface StatsResponse {
  individual: MonthlyStats[];
  byCategory: CategoryStats[];
}

interface UserSettingsResponse {
  pch_hours_prestataire: number;
  pch_hours_aidant: number;
  pch_hours_emploi_direct: number;
}

const MonthlyStatistics: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<'categories' | 'auxiliaires' | 'projections'>('categories');
  const [individualStats, setIndividualStats] = useState<MonthlyStats[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  // Per-category settings (forfaits) fetched from backend
  const [monthlyForfait, setMonthlyForfait] = useState<{ prestataire: number; aidant: number; emploi: number }>({ prestataire: 0, aidant: 0, emploi: 0 });
  // Per-category current solde inputs
  const [soldePrestataire, setSoldePrestataire] = useState<string>('');
  const [soldeAidant, setSoldeAidant] = useState<string>('');
  const [soldeEmploi, setSoldeEmploi] = useState<string>('');
  // Next 6 months usage per category
  const [nextSixCategoryUsages, setNextSixCategoryUsages] = useState<Record<string, number[]>>({});
  // Current month usage per category (for end-of-current-month calculation)
  const [currentMonthCategoryUsage, setCurrentMonthCategoryUsage] = useState<Record<string, number>>({});
  // Projections window offset (in months) relative to maintenant
  const [projectionOffset, setProjectionOffset] = useState<number>(0);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        const { data } = await apiClient.get<UserSettingsResponse>('/api/users/settings');
        setMonthlyForfait({
          prestataire: Number(data.pch_hours_prestataire || 0),
          aidant: Number(data.pch_hours_aidant || 0),
          emploi: Number(data.pch_hours_emploi_direct || 0),
        });
      } catch (e) {
        // Leave defaults
      }
    };
    fetchUserSettings();
  }, []);

  useEffect(() => {
    // Always base projections on the actual current month, independent of UI selection
    const fetchProjectionWindows = async () => {
      const baseDate = addMonths(new Date(), projectionOffset);
      const baseMonth = baseDate.getMonth();
      const baseYear = baseDate.getFullYear();

      // Build month sequence: next six months after current
      const monthsToFetch: { m: number; y: number }[] = [];
      let m = baseMonth;
      let y = baseYear;
      for (let i = 0; i < 6; i++) {
        monthsToFetch.push({ m, y });
        m = (m + 1) % 12;
        if (m === 0) y += 1;
      }

      try {
        // Fetch base month (for end-of-base-month calculation)
        const currentResp = await apiClient.get<StatsResponse>(`/api/stats/monthly?month=${baseMonth + 1}&year=${baseYear}`);
        const currentUsage: Record<string, number> = {};
        currentResp.data.byCategory.forEach((stat) => {
          currentUsage[stat.category] = stat.totalHours;
        });
        setCurrentMonthCategoryUsage(currentUsage);

        // Fetch next six months
        const responses = await Promise.all(
          monthsToFetch.map(({ m, y }) => apiClient.get<StatsResponse>(`/api/stats/monthly?month=${m + 1}&year=${y}`))
        );
        const usageMap: Record<string, number[]> = {};
        responses.forEach((resp, idx) => {
          resp.data.byCategory.forEach((stat) => {
            if (!usageMap[stat.category]) usageMap[stat.category] = Array(6).fill(0);
            usageMap[stat.category][idx] = stat.totalHours;
          });
        });
        setNextSixCategoryUsages(usageMap);
      } catch (e) {
        setCurrentMonthCategoryUsage({});
        setNextSixCategoryUsages({});
      }
    };
    fetchProjectionWindows();
  }, [projectionOffset]);

  const fetchStats = async () => {
    try {
      const { data } = await apiClient.get<StatsResponse>(`/api/stats/monthly?month=${selectedMonth + 1}&year=${selectedYear}`);
      setIndividualStats(data.individual);
      setCategoryStats(data.byCategory);
    } catch (error) {
      console.error('Error fetching monthly statistics:', error);
    }
  };

  const months = useMemo(
    () => [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ],
    []
  );

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  // Removed unused totalHoursSelectedMonth
  // Projections use the next 6 months usage per category

  // Helpers for per-category projections
  // Removed unused categoryUsageMap

  const selectedMonthLabel = useMemo(() => months[selectedMonth], [selectedMonth, months]);
  const selectedMonthTitle = useMemo(() => `${selectedMonthLabel} ${selectedYear}`, [selectedMonthLabel, selectedYear]);

  const categoryTotals = useMemo(() => {
    const totalHours = categoryStats.reduce((sum, s) => sum + s.totalHours, 0);
    const totalCost = categoryStats.reduce((sum, s) => sum + s.totalCost, 0);
    return { totalHours, totalCost };
  }, [categoryStats]);

  const individualTotals = useMemo(() => {
    const totalHours = individualStats.reduce((sum, s) => sum + s.totalHours, 0);
    const totalCost = individualStats.reduce((sum, s) => sum + s.totalCost, 0);
    return { totalHours, totalCost };
  }, [individualStats]);

  const projectCategoryBalances = useCallback((
    currentBalance: number,
    monthlyTopUp: number,
    nextSixUsages: number[],
    monthSeq: { monthIndex: number; year: number }[],
  ) => {
    const projections: { month: string; year: number; balance: string }[] = [];
    let bal = currentBalance;
    for (let i = 0; i < 6; i++) {
      const usage = nextSixUsages[i] || 0;
      const topUp = Number.isFinite(monthlyTopUp) ? monthlyTopUp : 0;
      bal = bal + topUp - usage;
      const labelMonthIndex = monthSeq[i]?.monthIndex ?? 0;
      const labelYear = monthSeq[i]?.year ?? new Date().getFullYear();
      projections.push({ month: months[labelMonthIndex], year: labelYear, balance: bal.toFixed(2) });
    }
    return projections;
  }, [months]);

  const nextUsagePrestataire = useMemo(
    () => nextSixCategoryUsages['Prestataire'] || [],
    [nextSixCategoryUsages]
  );
  const nextUsageAidant = useMemo(
    () => nextSixCategoryUsages['Aidant familial'] || [],
    [nextSixCategoryUsages]
  );
  const nextUsageEmploi = useMemo(
    () => nextSixCategoryUsages['Emploi direct'] || [],
    [nextSixCategoryUsages]
  );

  // Current month label and end-of-current-month balances based on real current month
  const baseDateForLabel = addMonths(new Date(), projectionOffset);
  const currentMonthIndexForLabel = baseDateForLabel.getMonth();
  const currentYearForLabel = baseDateForLabel.getFullYear();
  const currentUsagePrestataire = currentMonthCategoryUsage['Prestataire'] || 0;
  const currentUsageAidant = currentMonthCategoryUsage['Aidant familial'] || 0;
  const currentUsageEmploi = currentMonthCategoryUsage['Emploi direct'] || 0;

  const endOfCurrentPrestataire = soldePrestataire !== ''
    ? (parseFloat(soldePrestataire) || 0) - currentUsagePrestataire
    : null;
  const endOfCurrentAidant = soldeAidant !== ''
    ? (parseFloat(soldeAidant) || 0) - currentUsageAidant
    : null;
  const endOfCurrentEmploi = soldeEmploi !== ''
    ? (parseFloat(soldeEmploi) || 0) - currentUsageEmploi
    : null;

  // Build month sequence labels for the next six months starting from next month after current
  const projectionMonths: { monthIndex: number; year: number }[] = useMemo(() => {
    const seq: { monthIndex: number; year: number }[] = [];
    let m = currentMonthIndexForLabel;
    let y = currentYearForLabel;
    for (let i = 0; i < 6; i++) {
      seq.push({ monthIndex: m, year: y });
      m = (m + 1) % 12;
      if (m === 0) y += 1;
    }
    return seq;
  }, [currentMonthIndexForLabel, currentYearForLabel]);

  const startingPrestataire = endOfCurrentPrestataire ?? null;
  const startingAidant = endOfCurrentAidant ?? null;
  const startingEmploi = endOfCurrentEmploi ?? null;

  const projPrestataire = useMemo(() => {
    if (startingPrestataire === null) return [] as { month: string; year: number; balance: string }[];
    return projectCategoryBalances(
      startingPrestataire,
      Number(monthlyForfait.prestataire || 0),
      nextUsagePrestataire,
      projectionMonths,
    );
  }, [startingPrestataire, monthlyForfait.prestataire, nextUsagePrestataire, projectionMonths, projectCategoryBalances]);

  const projAidant = useMemo(() => {
    if (startingAidant === null) return [] as { month: string; year: number; balance: string }[];
    return projectCategoryBalances(
      startingAidant,
      Number(monthlyForfait.aidant || 0),
      nextUsageAidant,
      projectionMonths,
    );
  }, [startingAidant, monthlyForfait.aidant, nextUsageAidant, projectionMonths, projectCategoryBalances]);

  const projEmploi = useMemo(() => {
    if (startingEmploi === null) return [] as { month: string; year: number; balance: string }[];
    return projectCategoryBalances(
      startingEmploi,
      Number(monthlyForfait.emploi || 0),
      nextUsageEmploi,
      projectionMonths,
    );
  }, [startingEmploi, monthlyForfait.emploi, nextUsageEmploi, projectionMonths, projectCategoryBalances]);

  const projectionHeaderLabels = useMemo(
    () => projectionMonths.map(({ monthIndex, year }) => `${months[monthIndex]} ${year}`),
    [projectionMonths, months]
  );

  const projectionRows = useMemo(
    () => [
      { label: 'Prestataire', values: projPrestataire.map((p) => parseFloat(p.balance)) },
      { label: 'Aidant familial', values: projAidant.map((p) => parseFloat(p.balance)) },
      { label: 'Emploi direct', values: projEmploi.map((p) => parseFloat(p.balance)) },
    ],
    [projPrestataire, projAidant, projEmploi]
  );

  const getCategoryColor = (category: string) => {
    const colors = {
      'Aide à domicile': theme.palette.primary.main,
      'Soins infirmiers': theme.palette.secondary.main,
      'Ménage': theme.palette.success.main,
      'Autre': theme.palette.info.main,
    };
    return colors[category as keyof typeof colors] || theme.palette.grey[500];
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Mois</InputLabel>
              <Select value={selectedMonth} label="Mois" onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                {months.map((month, index) => (
                  <MenuItem key={month} value={index}>{month}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Année</InputLabel>
              <Select value={selectedYear} label="Année" onChange={(e) => setSelectedYear(Number(e.target.value))}>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minHeight: 42, '& .MuiTab-root': { textTransform: 'none', minHeight: 42 } }}
          >
            <Tab label="Par catégorie" value="categories" />
            <Tab label="Par auxiliaire" value="auxiliaires" />
            <Tab label="Projections" value="projections" />
          </Tabs>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Période</Typography>
            <Typography variant="h6" sx={{ fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 700 }}>{selectedMonthTitle}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: 'linear-gradient(90deg,#3b82f6 0%,#4f46e5 100%)', color: '#fff' }}>
          <CardContent>
            <Typography variant="overline" sx={{ opacity: 0.9 }}>Total heures</Typography>
            <Typography variant="h6">{(activeTab === 'categories' ? categoryTotals.totalHours : individualTotals.totalHours).toFixed(2)} h</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: 'linear-gradient(90deg,#0ea5e9 0%,#22c55e 100%)', color: '#fff' }}>
          <CardContent>
            <Typography variant="overline" sx={{ opacity: 0.9 }}>Coût total</Typography>
            <Typography variant="h6">€ {(activeTab === 'categories' ? categoryTotals.totalCost : individualTotals.totalCost).toFixed(2)}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {activeTab === 'categories' && (
        <>
          <Typography variant="h5" sx={{ mt: 3, mb: 2, color: theme.palette.primary.main, fontWeight: 'bold', fontFamily: 'Poppins, Inter, sans-serif' }}>
            Statistiques par catégorie
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Catégorie</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Heures totales</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Heures de nuit (22h-7h)</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Heures de dimanche</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Coût total (€)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryStats.map((stat) => (
                  <TableRow key={stat.category} sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover }, '&:hover': { backgroundColor: theme.palette.action.selected }, transition: 'background-color 0.2s' }}>
                    <TableCell component="th" scope="row" sx={{ color: getCategoryColor(stat.category) }}>{stat.category}</TableCell>
                    <TableCell align="right">{stat.totalHours.toFixed(2)}</TableCell>
                    <TableCell align="right">{stat.nightHours.toFixed(2)}</TableCell>
                    <TableCell align="right">{stat.sundayHours.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>{stat.totalCost.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {activeTab === 'projections' && (
        <>
          <Typography variant="h5" sx={{ mt: 3, mb: 2, color: theme.palette.primary.main, fontWeight: 'bold', fontFamily: 'Poppins, Inter, sans-serif' }}>
            Soldes et projections PCH (par catégorie)
          </Typography>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={`Solde actuel Prestataire (h) — Forfait mensuel: ${monthlyForfait.prestataire.toFixed(2)}h`}
                  type="number"
                  inputMode="decimal"
                  value={soldePrestataire}
                  onChange={(e) => setSoldePrestataire(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={`Solde actuel Aidant familial (h) — Forfait mensuel: ${monthlyForfait.aidant.toFixed(2)}h`}
                  type="number"
                  inputMode="decimal"
                  value={soldeAidant}
                  onChange={(e) => setSoldeAidant(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={`Solde actuel Emploi direct (h) — Forfait mensuel: ${monthlyForfait.emploi.toFixed(2)}h`}
                  type="number"
                  inputMode="decimal"
                  value={soldeEmploi}
                  onChange={(e) => setSoldeEmploi(e.target.value)}
                />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Fenêtre de projection</Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label="-"
                  onClick={() => setProjectionOffset((o) => Math.max(0, o - 1))}
                  onDelete={undefined as any}
                  deleteIcon={<ChevronLeftIcon />}
                  variant="outlined"
                  clickable
                  disabled={projectionOffset === 0}
                  sx={{ cursor: projectionOffset === 0 ? 'not-allowed' : 'pointer' }}
                />
                <Chip
                  label="Aujourd’hui"
                  onClick={() => setProjectionOffset(0)}
                  variant="outlined"
                  clickable
                  disabled={projectionOffset === 0}
                />
                <Chip
                  label="+"
                  onClick={() => setProjectionOffset((o) => o + 1)}
                  onDelete={undefined as any}
                  deleteIcon={<ChevronRightIcon />}
                  variant="outlined"
                  clickable
                />
              </Stack>
            </Stack>
          </Paper>

          <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Catégorie</TableCell>
                  {projectionHeaderLabels.map((label) => (
                    <TableCell key={label} align="right" sx={{ whiteSpace: 'nowrap' }}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {projectionRows.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                    {row.values.map((val, idx) => (
                      <TableCell
                        key={idx}
                        align="right"
                        sx={{
                          color: val < 0 ? 'error.main' : 'inherit',
                          backgroundColor: (theme) => (val < 0 ? alpha(theme.palette.error.main, 0.12) : 'transparent'),
                          fontWeight: val < 0 ? 700 : 400,
                        }}
                      >
                        {isNaN(val) ? '—' : `${val.toFixed(2)} h`}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {activeTab === 'auxiliaires' && (
        <>
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: theme.palette.primary.main, fontWeight: 'bold', fontFamily: 'Poppins, Inter, sans-serif' }}>
            Statistiques par auxiliaire
          </Typography>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Auxiliaire</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Catégorie</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Heures totales</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Heures de nuit (22h-7h)</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Heures de dimanche</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Taux horaire (€)</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Taux de nuit (€)</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Coût total (€)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {individualStats.map((stat) => (
                  <TableRow key={stat.auxiliaire} sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover }, '&:hover': { backgroundColor: theme.palette.action.selected }, transition: 'background-color 0.2s' }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>{stat.auxiliaire}</TableCell>
                    <TableCell sx={{ color: getCategoryColor(stat.category) }}>{stat.category}</TableCell>
                    <TableCell align="right">{stat.totalHours.toFixed(2)}</TableCell>
                    <TableCell align="right">{stat.nightHours.toFixed(2)}</TableCell>
                    <TableCell align="right">{stat.sundayHours.toFixed(2)}</TableCell>
                    <TableCell align="right">{stat.hourlyRate.toFixed(2)}</TableCell>
                    <TableCell align="right">{stat.nightlyRate.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>{stat.totalCost.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* CESU section removed as requested */}
    </Box>
  );
};

export default MonthlyStatistics; 
